import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, ArrowLeft, Check, Send, Loader2, ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import industries from "../data/industryData";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { PrivacyNotice } from "./PrivacyNotice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ id, bg = "bg-white", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${bg}`}>
    <div className="max-w-7xl mx-auto px-6">{children}</div>
  </section>
);

/* Customer Logo Carousel */
const CustomerCarousel = ({ customers, color, industryName }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const next = useCallback(() => setCurrent((p) => (p + 1) % customers.length), [customers.length]);
  const prev = () => setCurrent((p) => (p - 1 + customers.length) % customers.length);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 3000);
    return () => clearInterval(t);
  }, [paused, next]);

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <span className="text-sm font-bold uppercase tracking-widest mb-3 block" style={{ color }}>Trusted By</span>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#2C2A2B]">Our {industryName} Customers</h2>
        </div>

        {/* Carousel */}
        <div className="relative max-w-xl mx-auto" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="overflow-hidden rounded-2xl border border-[#E0D1BB]/30 bg-[#FAFAF7]">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${current * 100}%)` }}>
              {customers.map((c, i) => (
                <div key={i} className="w-full flex-shrink-0 flex flex-col items-center justify-center py-10 px-8">
                  <img src={c.logo} alt={c.name} className="h-20 w-auto object-contain mb-4" />
                  <h3 className="text-lg font-bold text-[#2C2A2B]">{c.name}</h3>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-5">
            <button onClick={prev} className="w-9 h-9 rounded-full border border-[#E0D1BB]/40 flex items-center justify-center text-[#6B6567] hover:border-[#308795] hover:text-[#308795] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1.5">
              {customers.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all duration-300 ${i === current ? "w-5" : "w-2"}`} style={{ backgroundColor: i === current ? color : "#D5CFC9" }} />
              ))}
            </div>
            <button onClick={next} className="w-9 h-9 rounded-full border border-[#E0D1BB]/40 flex items-center justify-center text-[#6B6567] hover:border-[#308795] hover:text-[#308795] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Logo strip */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
          {customers.map((c, i) => (
            <img key={i} src={c.logo} alt={c.name} className="h-10 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity" />
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactForm = ({ data }) => {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", company: "", website: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) { toast.error("Please fill in your first name, last name, and email."); return; }
    setLoading(true);
    try {
      const mp=[]; if(formData.company) mp.push(`Company: ${formData.company}`); if(formData.website) mp.push(`Website: ${formData.website}`); if(formData.message) mp.push(formData.message);
      await axios.post(`${API}/enquiries`, { name: `${formData.firstName} ${formData.lastName}`, email: formData.email, message: mp.join("\n") || null, selected_path: data.ctaPath });
      setSubmitted(true); toast.success("Thank you! We'll be in touch shortly.");
    } catch { toast.error("Something went wrong."); } finally { setLoading(false); }
  };
  if (submitted) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-full bg-[#7EA13E]/20 flex items-center justify-center mx-auto mb-5">
        <svg className="w-8 h-8 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
      </div>
      <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
      <p className="text-white/70">We'll be in touch shortly.</p>
    </div>
  );
  return (
    <div className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] rounded-2xl p-8">
      <h3 className="text-xl font-bold text-white mb-6">Book a Consultation</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div><label className="block text-sm font-medium text-white/70 mb-1.5">First Name & Last Name</label><div className="grid grid-cols-2 gap-3"><input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-all" placeholder="First name" /><input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-all" placeholder="Last name" /></div></div>
        <div><label className="block text-sm font-medium text-white/70 mb-1.5">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-all" placeholder="your@email.com" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium text-white/70 mb-1.5">Name of Company</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-all" placeholder="Company name" /></div>
          <div><label className="block text-sm font-medium text-white/70 mb-1.5">Website</label><input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-all" placeholder="https://www.example.com" /></div>
        </div>
        <div><label className="block text-sm font-medium text-white/70 mb-1.5">Message (Optional)</label><textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={3} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-all resize-none" placeholder="Tell us about your needs..." /></div>
        <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} dark />
        <Button type="submit" disabled={loading || !privacyConsent} className="w-full text-white font-semibold py-6 rounded-xl transition-all duration-300 text-base disabled:opacity-60" style={{ backgroundColor: data.color }}>
          {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : (<>Get Started<Send className="ml-2 h-4 w-4" /></>)}
        </Button>
      </form>
    </div>
  );
};

export default function IndustrySubPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = industries[slug];

  if (!data) return (
    <>
      <Header />
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#2C2A2B] mb-4">Industry page not found</h1>
          <Link to="/" className="text-[#308795] font-semibold hover:underline">Back to Home</Link>
        </div>
      </div>
      <Footer />
    </>
  );

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className={`relative min-h-[80vh] flex flex-col justify-center ${data.heroBg} overflow-hidden`}>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full -translate-y-1/3 translate-x-1/3" style={{ backgroundColor: data.color + "10" }} />
          <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24 w-full">
            <div className="flex items-center justify-between mb-8">
              <button onClick={() => { navigate('/'); setTimeout(() => { const el = document.getElementById('industries'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 300); }} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#E5BD77] transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Industries
              </button>
              <div className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-1.5 rounded-full" style={{ backgroundColor: data.color + "25", color: data.color }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: data.color }} />
                {data.eyebrow}
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-white leading-[1.1] tracking-tight mb-5 max-w-4xl">{data.headline}</h1>
            <p className="text-lg lg:text-xl text-white/60 leading-relaxed max-w-2xl mb-12">{data.subheadline}</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {data.stats.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.08] rounded-xl px-5 py-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: data.color + "20" }}>
                      <Icon className="w-5 h-5" style={{ color: data.color }} />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-white">{s.value}</div>
                      <div className="text-xs text-white/50 font-medium">{s.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Customer Carousel (if customers exist) */}
        {data.customers && data.customers.length > 0 && (
          <CustomerCarousel customers={data.customers} color={data.color} industryName={data.eyebrow.replace(" Solutions", "")} />
        )}

        {/* Content Sections */}
        {data.sections.map((sec, i) => (
          <Section key={i} bg={i % 2 === 0 ? "bg-[#FAFAF7]" : "bg-white"}>
            <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
              <div>
                <span className="text-sm font-bold uppercase tracking-widest mb-3 block" style={{ color: data.color }}>
                  {i === 0 ? "Core Capability" : i === 1 ? "Operations" : "Intelligence"}
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{sec.title}</h2>
                <p className="text-[#6B6567] text-lg leading-relaxed mb-6">{sec.description}</p>
                {sec.businessValue && (
                  <div className="bg-white border border-[#E0D1BB]/30 rounded-xl p-5">
                    <h4 className="text-sm font-bold text-[#7EA13E] uppercase tracking-wider mb-3">Business Value</h4>
                    <div className="space-y-2">
                      {sec.businessValue.map((v, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
                          <span className="text-sm text-[#494546]">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-8 lg:mt-0">
                <div className="grid grid-cols-1 gap-3">
                  {sec.items.map((item, j) => (
                    <div key={j} className="flex items-start gap-3 bg-white border border-[#E0D1BB]/20 rounded-xl px-5 py-3 transition-all duration-300 hover:shadow-sm">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: data.color }} />
                      <span className="text-sm text-[#494546]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Section>
        ))}

        {/* Extra Section (ESG only) */}
        {data.extraSection && (
          <Section bg="bg-white">
            <div className="max-w-3xl">
              <span className="text-sm font-bold uppercase tracking-widest mb-3 block" style={{ color: data.color }}>Impact</span>
              <h2 className="text-3xl font-bold text-[#2C2A2B] leading-tight mb-6">{data.extraSection.title}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.extraSection.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#FAFAF7] border border-[#E0D1BB]/20 rounded-xl px-5 py-3">
                    <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#494546]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* Why Trust */}
        <Section bg="bg-[#465155]">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">Why Asterisk</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-6">Trusted Expertise</h2>
              <div className="space-y-3">
                {data.whyTrust.map((t, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-4">
                    <Check className="w-5 h-5 text-[#E5BD77] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#D5CFC9]">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-10 lg:mt-0">
              <ContactForm data={data} />
            </div>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
